import { experienceUrl } from '@/lib/urls';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Building2, Search, Gift, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';

const STATE_NAMES = { co: 'Colorado', ca: 'California', tx: 'Texas', ny: 'New York', fl: 'Florida' };

export default function ExperienceBrowseByLocation() {
  const { state, city, neighborhood, venueSlug } = useParams();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = new URLSearchParams();
        if (city) params.set('city', city.charAt(0).toUpperCase() + city.slice(1));
        if (neighborhood) params.set('neighborhood', neighborhood);
        const res = await api.get(`/discover/experiences?${params.toString()}`);
        setExperiences(res.data.experiences || []);
      } catch { setExperiences([]); }
      finally { setLoading(false); }
    };
    fetch();
  }, [state, city, neighborhood]);

  const filtered = experiences.filter(e => !search || e.title?.toLowerCase().includes(search.toLowerCase()));

  const title = neighborhood
    ? `${neighborhood.replace(/-/g, ' ')} Experiences`.replace(/\b\w/g, l => l.toUpperCase())
    : city ? `${city.charAt(0).toUpperCase() + city.slice(1)} Experiences`
    : `${STATE_NAMES[state?.toLowerCase()] || state?.toUpperCase()} Experiences`;

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
          <Link to="/experiences" className="hover:text-white">Experiences</Link>
          {state && <><span>/</span><Link to={`/experiences/${state}`} className="hover:text-white">{(STATE_NAMES[state?.toLowerCase()] || state)?.toUpperCase()}</Link></>}
          {city && <><span>/</span><Link to={`/experiences/${state}/${city}`} className="hover:text-white">{city.charAt(0).toUpperCase() + city.slice(1)}</Link></>}
          {neighborhood && <><span>/</span><span className="text-white">{neighborhood.replace(/-/g, ' ')}</span></>}
        </div>

        <h1 className="text-4xl font-bold text-white mb-2">{title}</h1>
        <p className="text-slate-400 mb-8">{filtered.length} experience{filtered.length !== 1 ? 's' : ''} found</p>

        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input placeholder="Search experiences..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 h-12" />
        </div>

        {loading ? <div className="text-center py-20 text-slate-400">Loading...</div> : filtered.length === 0 ? (
          <div className="text-center py-20"><Gift className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-400">No experiences found.</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(exp => (
              <Link key={exp.id} to={experienceUrl(exp)}>
                <Card className="bg-slate-800/50 border-slate-700 hover:border-purple-500 transition-all overflow-hidden group h-full">
                  <div className="aspect-video bg-slate-700 overflow-hidden">
                    {exp.images?.[0] ? <img src={exp.images[0]} alt={exp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    : <div className="w-full h-full flex items-center justify-center"><Gift className="w-12 h-12 text-slate-600" /></div>}
                    {exp.is_eventgoers_exclusive && <Badge className="absolute top-2 right-2 bg-indigo-600 text-white border-0">EXCLUSIVE</Badge>}
                    {exp.is_date_night && <Badge className="absolute top-2 left-2 bg-pink-600 text-white border-0">DATE NIGHT</Badge>}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-white text-lg line-clamp-1">{exp.title}</h3>
                    {exp.hook && <p className="text-sm text-slate-400 mt-1 italic line-clamp-1">{exp.hook}</p>}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-purple-400 font-bold">${exp.price}</span>
                      <span className="text-xs text-slate-500">{exp.venue_name}</span>
                    </div>
                    {exp.vibe_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">{exp.vibe_tags.slice(0, 3).map(t => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
